import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeft, Plus, X, ShoppingCart, Loader, ChevronRight, Trash2 } from 'lucide-react';
import { getWeekMealPlans, assignRecipeToDate, removeMealPlan } from '../utils/mealPlanService';
import { getUserRecipes } from '../utils/recipeService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import BottomNav from '../components/BottomNav';

export default function Calendar() {
  const [weekDate, setWeekDate] = useState(new Date());
  const [mealPlans, setMealPlans] = useState({});
  const [recipes, setRecipes] = useState({});
  const [loading, setLoading] = useState(true);
  const [showRecipePicker, setShowRecipePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [allRecipes, setAllRecipes] = useState([]);
  const [saving, setSaving] = useState(false);
  
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Get week days
const getWeekDays = () => {
  const days = [];
  const start = new Date(weekDate);
  const day = start.getDay();
  const diff = start.getDate() - day + (day === 0 ? -6 : 1);
  
  const monday = new Date(start.getFullYear(), start.getMonth(), diff);
  monday.setHours(0, 0, 0, 0); // Set to midnight
  
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i);
    date.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
    days.push(date);
  }
  
  return days;
};

  const weekDays = getWeekDays();
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  useEffect(() => {
    loadWeekData();
  }, [weekDate, currentUser]);

  async function loadWeekData() {
  try {
    setLoading(true);
    
    console.log('Calendar loadWeekData - weekDate:', weekDate);
    
    const plans = await getWeekMealPlans(currentUser.uid, weekDate);
    console.log('Calendar received plans:', plans);
    
    setMealPlans(plans);
    
    const allUserRecipes = await getUserRecipes(currentUser.uid);
    setAllRecipes(allUserRecipes);
    
    const recipeIds = Object.values(plans).map(p => p.recipeId);
    const uniqueIds = [...new Set(recipeIds)];
    
    const recipeData = {};
    await Promise.all(
      uniqueIds.map(async (id) => {
        const recipeDoc = await getDoc(doc(db, 'recipes', id));
        if (recipeDoc.exists()) {
          recipeData[id] = { id: recipeDoc.id, ...recipeDoc.data() };
        }
      })
    );
    
    setRecipes(recipeData);
  } catch (error) {
    console.error('Error loading week data:', error);
  } finally {
    setLoading(false);
  }
}

  function formatDateKey(date) {
    return date.toISOString().split('T')[0];
  }

  function isToday(date) {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  function formatMonthYear(date) {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  function previousWeek() {
    const newDate = new Date(weekDate);
    newDate.setDate(newDate.getDate() - 7);
    setWeekDate(newDate);
  }

  function nextWeek() {
    const newDate = new Date(weekDate);
    newDate.setDate(newDate.getDate() + 7);
    setWeekDate(newDate);
  }

  function handleAddMeal(date) {
    setSelectedDate(date);
    setShowRecipePicker(true);
  }

async function handleSelectRecipe(recipeId) {
  if (!selectedDate) return;
  
  try {
    setSaving(true);
    const dateKey = formatDateKey(selectedDate);
    
    console.log('📅 Adding recipe to calendar:', { 
      recipeId, 
      selectedDate, 
      dateKey,
      currentUserId: currentUser.uid 
    });
    
    await assignRecipeToDate(currentUser.uid, selectedDate, recipeId);
    console.log('✅ Recipe assigned in Firestore');
    
    setMealPlans({
      ...mealPlans,
      [dateKey]: { recipeId, date: dateKey }
    });
    
    if (!recipes[recipeId]) {
      const recipeDoc = await getDoc(doc(db, 'recipes', recipeId));
      if (recipeDoc.exists()) {
        setRecipes({
          ...recipes,
          [recipeId]: { id: recipeDoc.id, ...recipeDoc.data() }
        });
      }
    }
    
    setShowRecipePicker(false);
    setSelectedDate(null);
  } catch (error) {
    console.error('Error assigning recipe:', error);
    alert('Failed to assign recipe. Please try again.');
  } finally {
    setSaving(false);
  }
}

  async function handleRemoveMeal(date) {
  const dateKey = formatDateKey(date);
  const plan = mealPlans[dateKey];
  
  if (!plan) return;
  
  try {
    console.log('🗑️ Remove single meal:', { date, dateKey });
    
    // Pass the original date object, don't recreate it
    await removeMealPlan(currentUser.uid, date);
    
    const newPlans = { ...mealPlans };
    delete newPlans[dateKey];
    setMealPlans(newPlans);
  } catch (error) {
    console.error('Error removing meal:', error);
    alert('Failed to remove meal. Please try again.');
  }
}

async function handleClearWeek() {
  if (!confirm('Remove all meals from this week? This cannot be undone.')) return;
  
  try {
    const entries = Object.entries(mealPlans);
    
    console.log('🧹 Clear Week - Starting...', { 
      entries, 
      entryCount: entries.length 
    });
    
    if (entries.length === 0) {
      alert('No meals to clear!');
      return;
    }
    
    // ✅ FIXED: Create Date objects without timezone issues
    const deletePromises = entries.map(([dateKey, plan]) => {
      // Parse date components manually to avoid timezone shift
      const [year, month, day] = dateKey.split('-').map(Number);
      const dateObj = new Date(year, month - 1, day); // month is 0-indexed
      console.log('🗑️ Deleting:', { dateKey, dateObj, plan });
      return removeMealPlan(currentUser.uid, dateObj);
    });
    
    console.log('⏳ Waiting for all deletes...');
    await Promise.all(deletePromises);
    console.log('✅ All deletes complete!');
    
    setMealPlans({});
    setRecipes({});
    
    alert(`Cleared ${entries.length} meal${entries.length !== 1 ? 's' : ''}!`);
  } catch (error) {
    console.error('❌ Error clearing week:', error);
    alert('Failed to clear week. Please try again.');
  }
}

  const plannedCount = Object.values(mealPlans).filter(plan => plan && plan.recipeId).length;

  return (
    <div className="min-h-screen pb-24" style={{ background: 'linear-gradient(135deg, #f8f0f5 0%, #e8e4f3 100%)' }}>
      {/* Flowing Blob Header - NOT STICKY */}
      <div className="relative h-80 overflow-hidden mb-6">
        <div 
          className="absolute w-96 h-96 rounded-full opacity-90"
          style={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
            top: '-80px',
            left: '-80px',
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            animation: 'morph 8s ease-in-out infinite',
          }}
        />
        
        <div 
          className="absolute w-80 h-80 rounded-full opacity-90"
          style={{
            background: 'linear-gradient(135deg, #fb7185 0%, #fda4af 100%)',
            top: '80px',
            right: '-100px',
            borderRadius: '60% 40% 30% 70% / 50% 60% 40% 50%',
            animation: 'morph 10s ease-in-out infinite reverse',
          }}
        />
        
        <div 
          className="absolute w-64 h-64 rounded-full"
          style={{
            background: 'linear-gradient(135deg, #ffe082 0%, #fff59d 100%)',
            bottom: '-60px',
            left: '100px',
            opacity: 0.85,
            borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%',
            animation: 'morph 12s ease-in-out infinite',
          }}
        />
        
        <div className="relative z-10 max-w-md mx-auto px-6 pt-12">
          <div className="flex items-center justify-between mb-2">
            <button 
              onClick={() => navigate('/')} 
              className="bg-white/20 backdrop-blur-sm p-2 rounded-xl hover:bg-white/30 transition-colors"
              style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            >
              <ChevronLeft size={24} className="text-white" />
            </button>
            <h1 
              className="text-white text-4xl logo"
              style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.2)' }}
            >
              This Week
            </h1>
            <div className="w-10" />
          </div>
          <p 
            className="text-white/95 text-base text-center mb-6"
            style={{ textShadow: '0 2px 10px rgba(0,0,0,0.2)' }}
          >
            {formatMonthYear(weekDate)}
          </p>
          
          {/* Week Navigation */}
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={previousWeek}
              className="bg-white/20 backdrop-blur-sm p-2 rounded-xl hover:bg-white/30 transition-colors"
              style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            >
              <ChevronLeft size={20} className="text-white" />
            </button>
            <span className="text-white font-semibold text-sm">Week View</span>
            <button
              onClick={nextWeek}
              className="bg-white/20 backdrop-blur-sm p-2 rounded-xl hover:bg-white/30 transition-colors"
              style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            >
              <ChevronRight size={20} className="text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Content - Added padding top for spacing */}
      <div className="max-w-md mx-auto px-6 pt-6">
        {/* Stats Card */}
        <div 
          className="p-6 rounded-3xl mb-6 text-center"
          style={{
            background: 'linear-gradient(135deg, #ede9fe 0%, #fce7f3 100%)',
            boxShadow: '0 10px 30px rgba(124, 58, 237, 0.1)',
          }}
        >
          <div className="text-4xl font-extrabold text-purple-600 mb-1">
            {plannedCount} / 7
          </div>
          <div className="text-sm text-gray-700 font-medium">Days Planned</div>
        </div>

        {/* Clear Week Button */}
        {plannedCount > 0 && (
          <button
            onClick={handleClearWeek}
            className="w-full mb-4 px-6 py-3 bg-red-50 text-red-600 rounded-2xl font-semibold border-2 border-red-200 hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
          >
            <Trash2 size={18} />
            Clear All Meals This Week
          </button>
        )}

        {/* Days */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader size={40} className="animate-spin text-purple-600" />
          </div>
        ) : (
          <div className="space-y-4 pb-6">
            {weekDays.map((date, index) => {
              const dateKey = formatDateKey(date);
              const plan = mealPlans[dateKey];
              const recipe = plan ? recipes[plan.recipeId] : null;
              const today = isToday(date);

              return (
                <div
                  key={dateKey}
                  className={`bg-white rounded-3xl p-5 shadow-md border-2 transition-all ${
                    plan 
                      ? 'border-purple-200 bg-purple-50'
                      : 'border-gray-100 hover:border-purple-200'
                  } ${today ? 'ring-2 ring-purple-600 ring-offset-2' : ''}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="font-bold text-gray-800 flex items-center gap-2">
                        {dayNames[index]}
                        {today && (
                          <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-lg font-semibold">
                            Today
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                    {plan ? (
                      <button
                        onClick={() => handleRemoveMeal(date)}
                        className="bg-red-500 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-red-600 transition-colors"
                      >
                        Remove
                      </button>
                    ) : (
                      <button
                        onClick={() => handleAddMeal(date)}
                        className="btn-primary px-4 py-2 text-sm"
                      >
                        <Plus size={16} /> Add Meal
                      </button>
                    )}
                  </div>

                  {plan && recipe ? (
                    <div className="pt-3 border-t-2 border-purple-200 border-dashed">
                      <div className="font-bold text-gray-800 mb-1">{recipe.name}</div>
                      <div className="text-sm text-gray-600">
                        {recipe.ingredients?.length || 0} ingredients
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-gray-400 text-sm italic py-4">
                      No meal planned yet
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Shopping List Button */}
        {plannedCount > 0 && (
          <button
            onClick={() => navigate('/shopping-list')}
            className="btn-primary w-full mb-6"
          >
            <ShoppingCart size={20} />
            Generate Shopping List
          </button>
        )}
      </div>

      {/* Recipe Picker Modal */}
      {showRecipePicker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold text-gray-800">Choose Recipe</h3>
                <button
                  onClick={() => {
                    setShowRecipePicker(false);
                    setSelectedDate(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                >
                  <X size={24} className="text-gray-600" />
                </button>
              </div>
              {selectedDate && (
                <p className="text-sm text-gray-600">
                  {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
              )}
            </div>

            <div className="overflow-y-auto max-h-96 p-6">
              {allRecipes.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-gray-600 mb-4">No recipes yet!</p>
                  <button
                    onClick={() => {
                      setShowRecipePicker(false);
                      navigate('/upload');
                    }}
                    className="text-purple-600 font-semibold hover:underline"
                  >
                    Upload a recipe →
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {allRecipes.map((recipe) => (
                    <button
                      key={recipe.id}
                      onClick={() => handleSelectRecipe(recipe.id)}
                      disabled={saving}
                      className="w-full bg-gray-50 hover:bg-purple-50 border-2 border-gray-200 hover:border-purple-300 rounded-2xl p-4 text-left transition-all disabled:opacity-50"
                    >
                      <div className="font-semibold text-gray-800">{recipe.name}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        {recipe.ingredients?.length || 0} ingredients
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
